import { LogIdentifierType } from '@/integrations/betterstack/enums/LogIndentifierType';
import LogService from '@/integrations/betterstack/LogService';
import { AuthUserEvent } from '@/modules/auth/events/AuthUserEvent';
import { AuthRoute } from '@/modules/auth/routes/auth.routes';
import { Route } from '@/modules/core/app/constants/api.constants';
import { HttpCustomStatus } from '@/modules/core/app/enums/HttpCustomStatus';
import EventBus from '@/modules/core/app/helpers/EventBus';
import { randomNumber } from '@/modules/core/app/helpers/NumberHelper';
import { AxiosRequestController } from '@/modules/core/app/models/AxiosRequestController';
import { CustomApiExceptionFactory } from '@/modules/core/app/models/CustomApiExceptionFactory';
import { ResourceAlreadyExistError } from '@/modules/core/app/models/ResourceAlreadyExistError';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import axios from 'axios';
import { camelizeKeys, decamelize } from 'humps';
import { flatten, isPlainObject } from 'lodash';
import { Env } from '/@/Env';
import SentryService from '/@/integrations/sentry/SentryService';
import { AUTH_STORAGE_KEY } from '/@/modules/auth/constants/auth.constants';
import ApiService from '/@/modules/core/api/services/ApiService';
import {
  CustomHeader,
  HttpStatus,
  PublicRoutes
} from '/@/modules/core/app/constants/api.constants';
import CacheHelper from '/@/modules/core/app/helpers/CacheHelper';
import Logger from '/@/modules/core/app/helpers/Logger';
import { getters } from '/@/modules/core/app/helpers/store';
import { ApiError } from '/@/modules/core/app/models/ApiError';
import { ModelCollection } from '/@/modules/core/app/models/ModelCollection';
import Lang from '/@/modules/core/app/services/Lang';
import NotificationService from '/@/modules/core/ui/services/NotificationService';

let _baseUrlRoot = null;

export const getBaseUrlRoot = () => {
  if (_baseUrlRoot) {
    return _baseUrlRoot;
  }

  if (Env.nodeEnv().VITE_APP_LOCAL_ORIGIN) {
    _baseUrlRoot = Env.nodeEnv().VITE_APP_LOCAL_ORIGIN;
    return _baseUrlRoot;
  }

  if (Env.isBrowser()) {
    const origin = window.location.origin;

    if (origin.includes('localhost')) {
      _baseUrlRoot = origin.replace('3000', '8080');
      return _baseUrlRoot;
    }

    _baseUrlRoot = origin.replace('app.', 'api.');
    return _baseUrlRoot;
  }

  return '';
};

export const resetBaseUrlCache = () => {
  _baseUrlRoot = null;
};

// Create axios instances without baseURL initially to avoid circular dependency
export const apiAxios = axios.create();

apiAxios.defaults.withCredentials = true;
apiAxios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Add request interceptor to set baseURL lazily
apiAxios.interceptors.request.use((config) => {
  if (!config.baseURL) {
    config.baseURL = `${getBaseUrlRoot()}/api`;
  }
  return config;
});

export const externalApiAxios = axios.create();

export const apiExternalV1Axios = axios.create();

apiExternalV1Axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Add request interceptor to set baseURL lazily
apiExternalV1Axios.interceptors.request.use((config) => {
  if (!config.baseURL) {
    config.baseURL = `${getBaseUrlRoot()}/api/v1`;
  }
  return config;
});

export const apiRequestInterceptor = (request) => {
  if (ApiService.shouldBlockRequest(request.url)) {
    throw new axios.Cancel();
  }

  const controller = new AbortController();
  request.signal = controller.signal;

  ApiService.addRequest(
    new AxiosRequestController({
      url: request.url,
      method: request.method,
      controller
    })
  );

  request.headers['request-id'] = randomNumber(100000, 999999).toString();

  request.headers['Accept-Language'] = Lang.locale;
  const i = getters.auth.getUser()?.i;
  if (i) {
    request.headers['Accept-Ip'] = i;
  }

  const socketId = getters.auth.getEchoInstance()?.socketId();
  if (socketId) {
    request.headers['X-Socket-ID'] = socketId;
  }

  try {
    const user = getters.auth.getUser();
    if (user?.id) {
      request.headers['X-User-Id'] = btoa(String(user.id));
    }
    if (user?.currentEstate?.id) {
      request.headers['X-Estate-Id'] = btoa(String(user.currentEstate.id));
    }
    if (user?.currentTeam?.id) {
      request.headers['X-Team-Id'] = btoa(String(user.currentTeam.id));
    }
  } catch (error) {
    Logger.error('Error injecting auth headers:', error);
  }

  if (Env.isBrowser()) {
    request.headers[CustomHeader.CLIENT_URL] = document.location.href;
  }

  if (request.data instanceof FormData) {
    request.headers['Content-Type'] = 'multipart/form-data';
  }

  if (request.data) {
    request.data = sanitizeRequest(request.data);
  }

  if (request.params) {
    request.params = sanitizeRequest(request.params);
  }

  if (getters.auth.shouldInjectEstate()) {
    request.headers[CustomHeader.ESTATE_ID] = getters.auth.getCurrentEstate().id;
  }

  return request;
};

export const apiResponseInterceptor = (response) => {
  if (!response) return;

  ApiService.removeRequest(response.config.url);

  let { data } = response;
  let result;

  data = _camelizePayload(response, data);

  if (data.error) {
    throw new ApiError(data);
  }

  if (response?.status) {
    _handleCustomStatus(response.status, data);
  }

  if (data instanceof Blob) {
    return response;
  }
  if (isPlainObject(data)) {
    if ('total' in data) {
      return data;
    }
    if ('data' in data) {
      return data.data;
    }
  }

  return data;
};

export const apiErrorHandler = async (error) => {
  if (axios.isCancel(error)) {
    throw error;
  }

  const response = error.response;

  if (!response) {
    // Network/CORS error (no response from server, e.g., Cloudflare block)
    NotificationService.show(Lang.t('core.there-rest-our'));
    SentryService.captureException(error);
    throw error;
  }

  ApiService.removeRequest(response.config.url);
  const apiError = new ApiError(response);

  switch (apiError.status) {
    case HttpCustomStatus.SESSION_MISMATCH:
      EventBus.signal(AuthUserEvent.SESSION_MISMATCH, response.data.url);
      break;
    case HttpStatus.UNPROCESSABLE_ENTITY:
      NotificationService.show(_flattenErrorValidator(apiError.errors));
      break;

    case HttpStatus.UNAUTHORIZED:
      try {
        if (apiError.config.url.includes(Route.AUTH_LOGIN)) {
          throw apiError;
        }
        if (response.config.url?.includes(Route.AUTH_REFRESHAUTHUSERTOKEN)) {
          LogService.info('Refresh auth token failed', LogIdentifierType.AUTH_REFRESH_TOKEN, {
            service_url: response.config.url,
            status: response.status
          });
          CacheHelper.remove(AUTH_STORAGE_KEY);
          const redirectUrl = encodeURIComponent(window.location.pathname);
          DocumentUIService.setLocation(`/${AuthRoute.LOGIN.path}?redirect=${redirectUrl}`);
          return;
        }
        await _refreshAuthToken();
        return apiAxios(response.config);
      } catch (refreshError) {
        if (!PublicRoutes.includes(apiError.config.url)) {
          CacheHelper.remove(AUTH_STORAGE_KEY);
          window.location.reload();
        } else if (!apiError.config.url?.includes(Route.AUTH_LOGIN)) {
          EventBus.signal(AuthUserEvent.USER_UNAUTHORIZED);
        }

        NotificationService.show([apiError.message]);
      }

      break;

    case HttpStatus.INTERNAL_SERVER_ERROR:
      NotificationService.show(
        Env.isProduction() ? Lang.t('core.there-rest-our') : apiError.message
      );
      break;
    case HttpStatus.CONFLICT:

    case HttpStatus.BAD_REQUEST:
    case HttpStatus.FORBIDDEN:
    case HttpStatus.NOT_FOUND:
    case HttpStatus.UNSUPPORTED_MEDIA_TYPE:
    case HttpStatus.TOO_MANY_REQUESTS:
      NotificationService.show([apiError.message]);
      break;
    default:
      Logger.log(apiError.message, Logger.LEVEL_ERROR);
  }

  SentryService.captureException(apiError);

  throw apiError;
};

/**
 * @param response
 * @param data
 * @returns {Object[]}
 * @private
 */
function _camelizePayload(response, data) {
  if (response.config?.url?.includes(Route.AUTH_ENVVALUES)) {
    return data;
  }

  // Convert api response from snake_case to camelCase
  if (data && response.headers && response.headers['content-type'] === 'application/json') {
    data = camelizeKeys(data);
  }
  return data;
}

/**
 * @param data
 * @returns {Array}
 * @private
 */
function _flattenErrorValidator(data) {
  return flatten(Object.values(data));
}

/**
 * Convert request from camelCase to snake_case and other post processing
 * @param object
 * @returns {*}
 */
export const sanitizeRequest = (object) => {
  if (object && !(object instanceof File)) {
    if (object instanceof Array) {
      return object.map((item) => sanitizeRequest(item));
    }

    if (object instanceof Date) {
      return object.toISOString();
    }

    if (object instanceof FormData) {
      const formData = new FormData();

      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of object.entries()) {
        formData.append(decamelize(key), value);
      }
      return formData;
    }

    if (object instanceof ModelCollection) {
      return object.get()?.map((item) => sanitizeRequest(item));
    }

    if (typeof object === 'object') {
      object = _removePoliciesFromRequest(object);
      object = _removeUnderscoredPropertiesFromRequest(object);
      return Object.keys(object).reduce(
        (acc, next) => ({
          ...acc,
          [decamelize(next)]: sanitizeRequest(object[next])
        }),
        {}
      );
    }
  }
  return object;
};

/**
 * @param data
 * @returns {Object[]}
 * @private
 */
function _removePoliciesFromRequest(data) {
  if (data.hasOwnProperty('policies')) {
    delete data.policies;
  }

  return data;
}

function _handleCustomStatus(status, data) {
  switch (status) {
    case HttpCustomStatus.RESOURCE_ALREADY_EXISTS:
      throw new ResourceAlreadyExistError();
    case HttpCustomStatus.CUSTOM_API_EXCEPTION:
      throw CustomApiExceptionFactory.build(data);
    default:
    // do nothing
  }
}

/**
 * @param data
 * @returns {{[p: string]: unknown}}
 * @private
 */
function _removeUnderscoredPropertiesFromRequest(data) {
  return Object.fromEntries(Object.entries(data).filter(([key]) => !key.startsWith('_')));
}

let isRefreshing = false;
let refreshPromise = null;

async function _refreshAuthToken() {
  if (isRefreshing) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = apiAxios.post(Route.AUTH_REFRESHAUTHUSERTOKEN).finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}
